import eaiws = egr.wcf.eaiws;
import catalog = egr.wcf.eaiws.catalog;
import utils = egr.wcf.utils;
import cf = egr.wcf.cf;
import core = egr.wcf.core;
import CatalogUI from './components/catalog';
import BasketUI from './components/basket';
import PropertyEditorUI from './components/property-editor';
import ViewerUI from './components/viewer';
import OapUI from './components/oap';
import ExportUI from './components/export';
import PersistenceUI from './components/persistence';
import ProgressUI from './components/progress';
import './index.css';
class App {
    protected readonly DEFAULT_GATEKEEPER_ID: string = '5a969885660e5';
    protected mSession: eaiws.EaiwsSession;
    protected mBasketUI: BasketUI;
    protected mViewerUI: ViewerUI;
    protected mCoreApp: core.Application;
    protected mArticleManager: cf.ArticleManager;
    protected mCatalogUI: CatalogUI;
    protected mPropertyEditorUI: PropertyEditorUI;
    protected mOapUI: OapUI;
    protected mExportUI: ExportUI;
    protected mPersistenceUI: PersistenceUI;

    public get coreApp(): core.Application {
        return this.mCoreApp;
    }

    public get articleManager(): cf.ArticleManager {
        return this.mArticleManager;
    }

    constructor() {
        void this.init();
    }

    public async init(): Promise<void> {
        if (!BABYLON.Engine.isSupported()) {
            alert('WebGL required!');
            return;
        }
        ProgressUI.beginLoading();
        // create session
        const tGatekeeperId: string = this.getGatekeeperId();
        try {
            const tGatekeeperResponse: { keepAliveInterval: number, server: string, sessionId: string } =
                await utils.async.ajax('GET', 'https://eaiws-server.pcon-solutions.com/v2/session/' + tGatekeeperId);
            this.mSession = new eaiws.EaiwsSession();
            this.mSession.connect(
                tGatekeeperResponse.server,
                tGatekeeperResponse.sessionId,
                tGatekeeperResponse.keepAliveInterval * 1000 // keep alive is given in seconds
            );
        } catch (e) {
            alert('Error while trying to setup session. Maybe wrong gatekeeper id?');
            return;
        }

        // setup languages (if first language is not available, the second will be used and so on)
        await this.mSession.catalog.setLanguages(['en', 'de', 'fr']);
        await this.mSession.basket.setLanguages(['en', 'de', 'fr']);

        // setup core
        this.mCoreApp = new core.Application();
        const tRootPath: string = utils.string.getFilePath(window.location.pathname);
        this.mCoreApp.dataPath = tRootPath + 'w-cf/data/';
        const tAppOptions: core.AppInitOptions = new core.AppInitOptions();
        this.mCoreApp.initialize(document.getElementById('viewer') as HTMLDivElement, tAppOptions);

        this.mArticleManager = new cf.ArticleManager(this.mCoreApp, this.mSession);
        this.mArticleManager.setGfjBasketIdsEnabled(true);

        // add all ui components
        this.mViewerUI = new ViewerUI(
            this.coreApp,
            {
                multiView: false,
                disableCameraPanning: false,
                limitCameraDistanceByElementRadius: false
            }
        );
        this.mCatalogUI = new CatalogUI(this.mSession.catalog, this.onInsertCatalogArticle.bind(this), this.onInsertCatalogContainer.bind(this));
        this.mPropertyEditorUI = new PropertyEditorUI(this.coreApp.model.selectionProperties);
        this.mOapUI = new OapUI(this.coreApp.model.selectionProperties, this.coreApp.appCallbacks);
        this.mBasketUI = new BasketUI(this.articleManager, this.onBasketItemClicked.bind(this));
        this.mExportUI = new ExportUI(this.articleManager, this.coreApp.viewer);
        this.mPersistenceUI = new PersistenceUI(this.articleManager, this.onLoadFile.bind(this));
        if (tGatekeeperId === this.DEFAULT_GATEKEEPER_ID) {
            await this.insertInitialArticle();
        }
        ProgressUI.endLoading();
    }

    /**
     * Gets gatekeeper id from user, by user input or by a given url parameter (?gatekeeper_id=XXXXXX).
     */
    private getGatekeeperId(): string {
        const tGateKeeperIdFromUrl: string | null = utils.string.getUrlParamValue(window.location.href, 'gatekeeper_id');
        let tGatekeeperId: string | null = tGateKeeperIdFromUrl;
        if (utils.string.isNullOrEmpty(tGatekeeperId)) {
            tGatekeeperId = prompt('please enter gatekeeper id:', this.DEFAULT_GATEKEEPER_ID);
            if (utils.string.isNullOrEmpty(tGatekeeperId)) { // fallback to default, if no valid input
                tGatekeeperId = this.DEFAULT_GATEKEEPER_ID;
            }
        }
        return tGatekeeperId;
    }

    private async onInsertCatalogArticle(pArticle: catalog.ArticleCatalogItem): Promise<void> {
        console.log('App::onInsertCatalogArticle', pArticle);
        ProgressUI.beginLoading();
        await this.removeAllElements();

        const tElement: cf.MainArticleElement = await this.mArticleManager.insertArticle(pArticle);
        this.mCoreApp.model.addElement(tElement); // we need to add it also to the model manager, or we wont see the new article
        this.mCoreApp.model.setSelection([tElement]);
        this.mViewerUI.resetCamera();
        this.mViewerUI.allowMainArticleSelection(this.mCoreApp.model.elements.length > 1);
        await this.mBasketUI.updateBasket();
        ProgressUI.endLoading();
    }

    private async onInsertCatalogContainer(pContainer: catalog.CatalogItem): Promise<void> {
        console.log('App::onInsertCatalogContainer', pContainer);
        ProgressUI.beginLoading();
        await this.removeAllElements();

        const tPecImport: cf.io.PecImport = new cf.io.PecImport(this.mArticleManager);
        const tElements: Array<core.mdl.SceneElement> = await tPecImport.importFromCatalog(pContainer);
        tElements.forEach((pElement) => this.mCoreApp.model.addElement(pElement));

        this.mViewerUI.resetCamera();
        this.mViewerUI.allowMainArticleSelection(this.mCoreApp.model.elements.length > 1);
        await this.mBasketUI.updateBasket();
        ProgressUI.endLoading();
    }

    private async removeAllElements(): Promise<void> {
        while (this.mCoreApp.model.elements.length > 0) {
            this.mCoreApp.model.removeElement(this.mCoreApp.model.elements[0]);
        }
        await this.mArticleManager.synchronizeSession(false); // we need to tell the server, that we deleted items
    }

    /**
     * Select article item if it was clicked in the basket.
     */
    private onBasketItemClicked(pItem: cf.ArticleElement, pEvent: MouseEvent): void {
        console.log('App::onBasketItemClicked', pItem);
        pEvent.stopImmediatePropagation();
        if (pItem instanceof cf.MainArticleElement) {
            this.coreApp.model.setSelection([pItem]);
        } else if (pItem instanceof cf.SubArticleElement) {
            this.coreApp.model.setSubElementSelection(pItem.getMainArticle(), pItem);
        }
    }

    /**
     * Loads an old planning.
     * @param pFile Loaded .obk/.pec file as binary
     */
    private async onLoadFile(pFile: Blob, pFileType: 'obk' | 'pec'): Promise<void> {
        ProgressUI.beginLoading();
        this.coreApp.document.clear(); // removes everything from the scene, so we can start from scratch
        if (pFileType === 'obk') {
            const tUrl: string | null = await this.articleManager.session.uploadFile('Project', pFile);
            if (utils.string.isNullOrEmpty(tUrl)) {
                alert('failed to upload file');
                return;
            }
            await this.mSession.session.loadSession(tUrl);
            await this.mSession.deleteFile(tUrl); // we don't need the file anymore
            await this.articleManager.importFromSession();
        } else if (pFileType === 'pec') {
            const tPecImport: cf.io.PecImport = new cf.io.PecImport(this.articleManager);
            try {
                const tElements: Array<core.mdl.SceneElement> = await tPecImport.import(pFile);
                const tInsertCommand: core.cmd.InsertElements = new core.cmd.InsertElements(this.coreApp, tElements);
                await this.coreApp.commands.executeCommand(tInsertCommand);
            } catch (e) {
                console.error('failed to load pec', e);
            }
        }
        this.mViewerUI.resetCamera();
        await this.mBasketUI.updateBasket();
        if (this.coreApp.model.elements.length > 0) {
            this.coreApp.model.setSelection([this.coreApp.model.elements[0]]);
        }
        this.mViewerUI.allowMainArticleSelection(this.mCoreApp.model.elements.length > 1);
        ProgressUI.endLoading();
    }

    /**
     * Example for inserting a specific configuration of an article.
     *
     * The key to get a specific configuration is the variant code.
     * He can be received by getArticleData() (i.e. type in console: (await app.coreApp.model.selection[0].getArticleData()).variantCode).
     * So you could configure an article as you want, get its variant code and insert him here.
     * The other data will be logged, if you insert an article by onCatalogArticleClicked().
     *
     * If you want to insert a more complex article with sub articles, you could save an .obk and load it on startup.
     */
    private async insertInitialArticle(): Promise<void> {
        await this.onInsertCatalogArticle({
            articlePackageId: '::egr::office2::1/ANY',
            baseArticleNumber: '4520',
            catalogId: 'egr:0',
            catalogNodeKey: 'office2/4520/default',
            catalogPackageId: '::egr::office2::1/ANY',
            icon: 'https://ipa-eaiws2.easterngraphics.com/OFML/data/N8ria7DfvFQ4LcnkivaNwg/egr/office2/ANY/1/image/4520___default.jpg',
            label: 'office swivel chair 2',
            name: '4520$default',
            resources: undefined,
            type: 'Article',
            varCodeType: 'OFML',
            variantCode: 'Polster.Ausf=13;Armlehne.Visible=1;Gestell.FootMat=82'
        });
    }
}
export default App;
window.onload = () => {
    (window as any).app = new App(); // make app accessible in console for debug/testing, just type "app" in the browser console
};